import { Divider, Grid } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { formatDecimal, formatPercentColumn, isMobileDevice } from "../../../commons/utils";
import BarChart from "../../../charts/Bar";
import { DataTable } from "../../../components/DataTable";
import { Columns } from "..";
import { ExPaper } from "../../../components/Paper";
import { DashboardPage } from "../../../components/DashboardPage";
import CircularLoading from "../../../components/CircularLoading";
import { SelectorMenu, SelectorOption } from "../../../components/SelectorMenu";
import { Percent, Weight } from "lucide-react";
import { FiltroBaseSafraPlantio } from "../FiltroBaseSafraPlantio";
import { agruparClassificacoes, agruparEntradas } from "../utils";

const options: SelectorOption[] = [
    { id: "embalagemclassificacao", name: "Embalagem", icon: <Weight size={20} /> },
    { id: "percentclassificacao", name: "Porcentagem", icon: <Percent size={20} /> },
];

export const DashClassificacoes = forwardRef((props: any, ref) => {
    const refFiltro = useRef<any>(null);
    const refClassificacoes = useRef<any>(null);

    const [originData, setOriginData] = useState<any[]>([]);
    const [originDataEntrada, setOriginDataEntrada] = useState<any[]>([]);
    const [movClassificacao, setMovClassificacao] = useState<any[]>([]);
    const [viewPecent, setViewPercent] = useState<any>(false);

    const columns = [
        { field: 'pldescricao', header: 'Plantio' },
        { field: 'totalembentrada', header: 'Embalagens Entrada', body: (e: any) => formatDecimal(e.totalembentrada), dataType: 'numeric' },
        { field: 'totalembclassificada', header: 'Embalagens Classificadas', body: (e: any) => formatDecimal(e.totalembclassificada), dataType: 'numeric' },
        { field: 'percentperdaemb', header: '% Perda Embalagem', body: (e: any) => formatPercentColumn(e.percentperdaemb), dataType: 'numeric' },
        { field: 'totalpesentrada', header: 'Pesagem Entrada', body: (e: any) => formatDecimal(e.totalpesentrada), dataType: 'numeric' },
        { field: 'totalpesclassificada', header: 'Pesagem Classificadas', body: (e: any) => formatDecimal(e.totalpesclassificada), dataType: 'numeric' },
        { field: 'percentperdapes', header: '% Perda Pesagem', body: (e: any) => formatPercentColumn(e.percentperdapes), dataType: 'numeric' }
    ];

    useImperativeHandle(ref, () => ({
        setData
    }));

    useEffect(() => {
        if (originData && originData.length > 0)
            refFiltro.current.refresh();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewPecent]);

    function prePopulate(classificacoes: any, entradas: any) {
        let group_entradas = agruparEntradas(entradas);
        let group_classificacoes = agruparClassificacoes(classificacoes);

        populateChart(group_classificacoes);
        populateGrid(group_entradas, group_classificacoes);
    }

    function populateChart(_data: any) {
        const groupClassificados = _data.reduce((acc: any, item: any) => {
            const key = item.culttamdescricao;
            if (!acc[key]) {
                acc[key] = { culttamdescricao: key, totalembalagem: 0, culttamsequencia: item.culttamsequencia };
            }
            acc[key].totalembalagem += item.totalembalagem;
            return acc;
        }, {});

        const _classificacoes = Object.values(groupClassificados).sort((a: any, b: any) => {
            if (a.culttamsequencia < b.culttamsequencia) return -1;
            if (a.culttamsequencia > b.culttamsequencia) return 1;
            return 0;
        });

        const tamanhos = _classificacoes.map((item: any) => parseFloat(item.totalembalagem.toFixed(2)));
        const descricoes = _classificacoes.map((item: any) => item.culttamdescricao);

        if (viewPecent) {
            const totalSum = tamanhos.reduce((sum: number, item: number) => sum + item, 0);
            const porcentagens = tamanhos.map((item: number) => parseFloat(((item / totalSum) * 100).toFixed(2)));

            refClassificacoes?.current?.setOptions(descricoes, 100, '% Classificadas por Tamanho', true);
            refClassificacoes?.current?.setSeries([{ name: '% Classificadas por Tamanho', data: porcentagens }]);
        } else {
            const getNextMultipleOf2000 = (value: number) => Math.ceil(value / 2000) * 2000;

            refClassificacoes?.current?.setOptions(descricoes, getNextMultipleOf2000(Math.max(...tamanhos)), 'Embalagens Classificadas por Tamanho');
            refClassificacoes?.current?.setSeries([{ name: 'Embalagens Classificadas por Tamanho', data: tamanhos }]);
        }
    }

    function populateGrid(_dataEntrada: any, _data: any) {
        const groupEntrada = _dataEntrada.reduce((acc: any, item: any) => {
            const key = item.pldescricao;
            if (!acc[key]) {
                acc[key] = { pldescricao: key, totalembentrada: 0, totalpesentrada: 0 };
            }
            acc[key].totalembentrada += item.totalembalagem;
            acc[key].totalpesentrada += item.totalpesagem;
            return acc;
        }, {});

        const groupClassificacao = _data.reduce((acc: any, item: any) => {
            const key = item.pldescricao;
            if (!acc[key]) {
                acc[key] = { pldescricao: key, totalembclassificada: 0, totalpesclassificada: 0 };
            }
            acc[key].totalembclassificada += item.totalembalagem;
            acc[key].totalpesclassificada += item.totalpesagem;
            return acc;
        }, {});

        const combinedData = Object.values(groupEntrada).map((entrada: any) => {
            const classificacao = groupClassificacao[entrada.pldescricao] || { totalembclassificada: 0 };

            const totalembentrada = entrada.totalembentrada;
            const totalpesentrada = entrada.totalpesentrada;
            const totalembclassificada = classificacao.totalembclassificada;
            const totalpesclassificada = classificacao.totalpesclassificada;
            const percentperdaemb = totalembentrada === 0 ? 0 : ((totalembentrada - totalembclassificada) / totalembentrada) * 100;
            const percentperdapes = totalpesentrada === 0 ? 0 : ((totalpesentrada - totalpesclassificada) / totalpesentrada) * 100;

            return {
                pldescricao: entrada.pldescricao,
                totalembentrada,
                totalpesentrada,
                totalembclassificada,
                totalpesclassificada,
                percentperdaemb,
                percentperdapes
            };
        });

        const sortedPlantio = combinedData.sort((a: any, b: any) => {
            if (a.pldescricao < b.pldescricao) return -1;
            if (a.pldescricao > b.pldescricao) return 1;
            return 0;
        });

        setMovClassificacao(sortedPlantio);
    }

    function setData(_dataEntrada: any, _data: any) {
        setOriginData(_data);
        setOriginDataEntrada(_dataEntrada);

        refFiltro.current.refresh(_data, _dataEntrada);
    }

    return (
        !originData ? <CircularLoading /> :
            <div style={{ padding: isMobileDevice() ? '1rem' : '0 4rem' }}>

                <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0 }}>
                    <FiltroBaseSafraPlantio
                        ref={refFiltro}
                        tab={'classificacao'}
                        data={originData}
                        entradas={originDataEntrada}
                        cultura={props?.cultura}
                        onFilterChange={({ data, entradas }) => { prePopulate(data, entradas) }}
                    />

                    <Grid item xs={isMobileDevice() ? 12 : 3} style={{ paddingTop: 0, height: '72px' }}>
                        <SelectorMenu
                            title="Visualizar"
                            options={options}
                            defaultSelectedId="embalagemclassificacao"
                            onSelect={(id) => setViewPercent(id === 'percentclassificacao')}
                        />
                    </Grid>

                    <Grid item xs={12} style={{ paddingTop: 0, marginTop: '12px' }}>
                        <Divider sx={{ backgroundColor: '#fff' }} />
                    </Grid>
                </Grid>

                <DashboardPage name='Classificação'>
                    <Grid container spacing={4} style={{ padding: '12px 0px' }}>
                        <Grid item xs={12}>
                            <BarChart ref={refClassificacoes} />
                        </Grid>
                        <Grid item xs={12}>
                            <ExPaper>
                                <DataTable value={movClassificacao} originvalues={movClassificacao} disabledrefresh={true}>
                                    {Columns(columns)}
                                </DataTable>
                            </ExPaper>
                        </Grid>
                    </Grid >
                </DashboardPage>
            </div>
    )
})